import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import { ExclamationCircleIcon, ClockIcon } from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import Card from "../layout/Card";
import {
  TriviaState,
  createTrivia,
  deleteTrivia,
  getTrivia,
  updateTrivia,
} from "../../slices/content";
import DangerZone from "../forms/DangerZone";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Trivia() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getTrivia());
  }, [dispatch]);

  const [selectedTrivia, setSelectedTrivia] = useState<{
    id: string;
    response: { [key: string]: string };
  }>({ id: "", response: { EN: "", FR: "", IT: "", DE: "" } });
  const [createTriviaForm, setCreateTriviaForm] = useState<{
    response: { [key: string]: string };
  }>({ response: { EN: "", FR: "", IT: "", DE: "" } });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { trivia } = useAppSelector((state) => state.content);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectTriviaToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [triviaId] = args;
    const triviaData = triviasData.filter((trv) => trv.id === triviaId)[0];
    setSelectedTrivia({
      id: triviaData.id,
      response: triviaData.response,
    });
    setShowEditModal(true);
  }

  function openCreateTriviaModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreateTrivia(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { response } = createTriviaForm;

    // Validate text fields
    if (response[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_text", language), "danger", 5000),
      );
      return;
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createTrivia({
        property: user.property.id,
        intent: "TRI",
        status: "SUCC",
        response,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_trivia", language), "success", 3000),
      );
      setCreateTriviaForm({ response: { EN: "", FR: "", IT: "", DE: "" } });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditTrivia(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { id, response } = selectedTrivia;

    // Validate text fields
    if (response[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_text", language), "danger", 5000),
      );
      return;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateTrivia({
        triviaId: id,
        property: user.property.id,
        intent: "TRI",
        status: "SUCC",
        response,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_trivia", language), "success", 3000),
      );
      setSelectedTrivia({
        id: "",
        response: { EN: "", FR: "", IT: "", DE: "" },
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteTrivia(e: any, args: any[]) {
    e.preventDefault();
    const [triviaIds] = args;
    if (triviaIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (triviaIds === null || triviaIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_trivia", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deleteTrivia({ triviaIds: [triviaIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  // Format rooms data and filter by query param tag
  const triviasData = trivia.data === null ? [] : trivia.data;

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("trivia_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateTriviaModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_trivia", language)}
              </Button>
            }
          />
        }
      />

      {/* Create trivia modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_trivia", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateTrivia}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_trivia", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Response */}
              {/* <TextArea
                id="response"
                label={translateText("text", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTriviaForm}
                stateName="response"
                onChange={setCreateTriviaForm}
              /> */}
              <TextInputMultiLanguage
                id="response"
                type="textarea"
                label={translateText("text", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTriviaForm}
                stateName="response"
                onChange={setCreateTriviaForm}
                defaultLanguage={language}
                languageChoices={
                  triviasData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(triviasData[0].response)
                }
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit trivia modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_trivia", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditTrivia}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_weblink", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Response */}
              {/* <TextArea
                id="response"
                label={translateText("text", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTrivia}
                stateName="response"
                onChange={setSelectedTrivia}
              /> */}
              <TextInputMultiLanguage
                id="response"
                type="textarea"
                label={translateText("text", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTrivia}
                stateName="response"
                onChange={setSelectedTrivia}
                defaultLanguage={language}
                languageChoices={
                  triviasData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(triviasData[0].response)
                }
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedTrivia.id}
                handler={confirmDeleteTrivia}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {trivia.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {triviasData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {triviasData.map((trv: TriviaState) => (
                    <a
                      href="#!"
                      key={trv.id}
                      onClick={(e) => selectTriviaToEdit(e, [trv.id])}
                      className="relative"
                    >
                      {/* Trivia data */}
                      <Card
                        key={trv.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <p
                                className={`text-base font-medium leading-6 text-black truncate`}
                              >
                                {trv.response[language]}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {trv.datetime_created}
                          </div>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_trivia", language)}
                subtext={translateText("no_trivia_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
